'use client';

import React from 'react';
import { interiorStyles, marketingStyles } from '@/components/marketing';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useScrollThreshold } from '@/hooks/useScrollThreshold';
import { discoveryCTAEvents } from '../PostHogProvider';
import { SCAN_JUMP_CTA_LABEL, SCAN_TRUST_LINE } from '@/lib/conversion-copy';
import type { DiscoveryPageSource } from '@/lib/discovery-source';

const CALM_EASE = [0.33, 0, 0.2, 1] as const;
const DISMISS_KEY = 'sticky-cta-dismissed';

interface StickyScrollCTAProps {
  /**
   * Page that hosts this bar. Required, and must be the SAME value the page
   * passes to its `InlineVisibilityChecker`, so the sticky-bar events join
   * with `discovery_cta_input_focused` on `source`.
   */
  source: DiscoveryPageSource;
  threshold?: number;
  visualVariant?: 'legacy' | 'articleV8' | 'marketing';
}

export function StickyScrollCTA({
  source,
  threshold = 50,
  visualVariant = 'legacy',
}: StickyScrollCTAProps): React.ReactElement {
  const isPastThreshold = useScrollThreshold(threshold);
  const shouldReduceMotion = useReducedMotion();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isNearChecker, setIsNearChecker] = useState(false);
  const [debouncedNearChecker, setDebouncedNearChecker] = useState(false);
  const hasTrackedShown = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Check sessionStorage on mount
  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === 'true') {
        setIsDismissed(true);
      }
    } catch {
      // sessionStorage unavailable (e.g. incognito in some browsers)
    }
  }, []);

  // Detect proximity to the inline checker
  useEffect(() => {
    const checker = document.getElementById('visibility-check');
    if (!checker) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsNearChecker(entry.isIntersecting);
      },
      { rootMargin: '300px 0px 300px 0px' }
    );

    observer.observe(checker);
    return () => observer.disconnect();
  }, []);

  // Debounce hiding so fast scrolling doesn't flash the bar
  useEffect(() => {
    if (isNearChecker) {
      // Delay hiding by 400ms so quick scroll-throughs don't flash
      hideTimerRef.current = setTimeout(() => {
        setDebouncedNearChecker(true);
      }, 400);
    } else {
      // Show immediately when scrolling away from checker
      clearTimeout(hideTimerRef.current);
      setDebouncedNearChecker(false);
    }
    return () => clearTimeout(hideTimerRef.current);
  }, [isNearChecker]);

  const isVisible = isPastThreshold && !isDismissed && !debouncedNearChecker;

  // Track first appearance
  useEffect(() => {
    if (isVisible && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      discoveryCTAEvents.stickyBarShown(source);
    }
  }, [isVisible, source]);

  const handleClick = useCallback(() => {
    discoveryCTAEvents.stickyBarClicked(source);
    const target = document.getElementById('visibility-check');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [source]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    discoveryCTAEvents.stickyBarDismissed(source);
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // sessionStorage unavailable
    }
  }, [source]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={shouldReduceMotion ? false : { y: '100%' }}
          animate={{ y: 0 }}
          exit={shouldReduceMotion ? undefined : { y: '100%' }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3, ease: CALM_EASE }}
          className={`fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur-sm ${
            visualVariant === 'marketing'
              ? interiorStyles.sticky
              : visualVariant === 'articleV8'
                ? 'border-[#EBE6DF] bg-[#FAF7F3]/95'
                : 'border-slate-200 bg-white/95 shadow-lg'
          }`}
          role="complementary"
          aria-label="AI visibility check prompt"
        >
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-6">
            <p className="min-w-0 text-sm text-slate-700 sm:text-base">
              <span>{SCAN_TRUST_LINE}</span>
            </p>

            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={handleClick}
                className={
                  visualVariant === 'marketing'
                    ? marketingStyles.btn
                    : `inline-flex items-center rounded-full bg-[#B45309] px-4 py-2 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-[#B45309] focus:ring-offset-2 ${
                        visualVariant === 'articleV8'
                          ? 'transition-colors duration-150 hover:bg-[#92400E] focus:ring-offset-[#FAF7F3]'
                          : 'transition-all duration-150 hover:scale-[1.02] hover:bg-[#92400E] active:scale-[0.98]'
                      }`
                }
              >
                <span>{SCAN_JUMP_CTA_LABEL}</span>
              </button>

              <button
                onClick={handleDismiss}
                className={
                  visualVariant === 'marketing'
                    ? interiorStyles.stickyDismiss
                    : 'rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600'
                }
                aria-label="Dismiss visibility prompt"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
